import React, { useEffect, useMemo, useRef, useState } from "react";
import RecordAvatar, { RecordImageKind } from "../RecordAvatar";
import ImageUpload, { FileUploadHandle } from "../ImageUpload";
import classes from "./record.module.scss";

interface PhotoFieldProps {
    /** Record name — the initials fallback. */
    name: string;
    /** The saved photo / logo URL. */
    src?: string | null;
    /** A newly chosen file (shown instead of `src` until saved). */
    file?: File | null;
    kind?: RecordImageKind;
    canEdit: boolean;
    onFileSelect: (file: File | null) => void;
    onRemoveFile?: () => void;
}

/**
 * The 96px photo / logo tile of an edit form's "Identity & status": the image
 * or the initials, a labelled Change photo button (crop dialog follows), Remove,
 * and drop-on-the-tile. Never the native file box or a grey placeholder.
 */
const PhotoField: React.FC<PhotoFieldProps> = ({ name, src, file, kind = "logo", canEdit, onFileSelect, onRemoveFile }) => {
    const upload = useRef<FileUploadHandle>(null);
    const [isOver, setIsOver] = useState(false);
    const preview = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);
    useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);
    // photoFile can hold a marker ("photo") rather than a URL while a new file is pending.
    const shown = preview ?? (src && /^(https?:|blob:|data:|\/)/.test(src) ? src : null);

    return (
        <div className={classes.photoField}>
            <div
                className={`${classes.photoTile} ${isOver ? classes.photoTileOver : ""}`}
                title={canEdit ? "Drop an image here, or use Change photo" : undefined}
                onDragOver={(event) => {
                    if (!canEdit) return;
                    event.preventDefault();
                    setIsOver(true);
                }}
                onDragLeave={() => setIsOver(false)}
                onDrop={(event) => {
                    if (!canEdit) return;
                    event.preventDefault();
                    setIsOver(false);
                    const dropped = event.dataTransfer.files?.[0];
                    if (dropped && dropped.type.startsWith("image/")) upload.current?.takeFile(dropped);
                }}
            >
                <RecordAvatar name={name || "?"} src={shown} kind={kind} />
            </div>
            <ImageUpload ref={upload} canEdit={canEdit} hasPhoto={!!shown} onFileSelect={onFileSelect} onRemoveFile={onRemoveFile} />
        </div>
    );
};

export default PhotoField;
